import React, { useState, useRef, useEffect, useLayoutEffect, useCallback, useMemo } from 'react';
import { X, BookOpen, FileText, Plus, Printer, Share2, Calendar, Camera, Trash2, Loader2, ArrowLeft, Maximize2, MessageSquare, CheckCircle2, RotateCcw, ChevronDown, ChevronUp } from 'lucide-react';
import { useCustomerNotes, type CustomerNote } from '../../../hooks/useCustomerNotes';
import { cn } from '../../../utils/cn';
import { condoApi, makeAbsolutePhotoUrl } from '../../../services/api';
import PhotoActionModal from '../../PhotoActionModal';
import PhotoUploadModal, { PhotoResult } from '../../PhotoUploadModal';
import PhotoWebcamModal from '../../PhotoWebcamModal';
import toast from 'react-hot-toast';
import { normalizePhotoForUpload } from '../../../utils/uploadPhoto';
import {
  DEFAULT_LIVRO_FILTER,
  LIVRO_ENTRY_MAX_CHARS,
  LIVRO_FILTERS,
  canCloseLivroPage,
  canReopenLivroPage,
  canSubmitEntry,
  counter as entryCounter,
  filterLabel,
  kindLabel,
  matching,
  orderedEntries,
  statusLabel,
  writingHint,
  type LivroFilter,
  type LivroNoteEntry,
} from './livroThread';

interface LivroPanelProps {
  customerId: string;
  /**
   * Back arrow in the header. Omitted on the standalone /livro page, where the app's own
   * navigation already provides "back" and a second one would be a dead control.
   */
  onClose?: () => void;
  lightTheme?: boolean;
  /**
   * Whether this reader may CLOSE pages, as opposed to write them.
   *
   * The portaria writes the book all shift and reads last night's; deciding an occurrence is
   * finished is the síndico's call. Both call sites derive it from the caller's roles
   * (`canManageLivro`) rather than from which surface they are — a síndico in the Operational View
   * is still a síndico. Defaults to true so a caller that has not decided is not silently
   * downgraded.
   */
  canManage?: boolean;
}

/** Inline composer bounds. Below MIN it looks like a single-line input and invites one-liners;
 *  past MAX it pushes the entry list off screen, which is when the expand modal takes over. */
const COMPOSER_MIN_PX = 76;
const COMPOSER_MAX_PX = 260;

/** Entry length bounds. The minimum keeps "ok" / "teste" out of what is a formal record; the
 *  maximum keeps one entry readable in the list and in the printed resumo. Both are enforced on
 *  the TRIMMED text, so spaces cannot buy either end. */
export const LIVRO_MIN_CHARS = 10;
export const LIVRO_MAX_CHARS = 1200;

type PresetFilter = 'active' | 'today' | 'yesterday' | '2days' | '7days' | 'custom';

function formatTimestamp(dateStr: string): string {
  const d = new Date(dateStr);
  return d.toLocaleDateString('pt-BR', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

// Auto-format as user types: digits only → dd/mm/yyyy
function formatDateTyped(raw: string): string {
  const d = raw.replace(/\D/g, '').slice(0, 8);
  if (d.length <= 2) return d;
  if (d.length <= 4) return `${d.slice(0, 2)}/${d.slice(2)}`;
  return `${d.slice(0, 2)}/${d.slice(2, 4)}/${d.slice(4)}`;
}

// dd/mm/yyyy → YYYY-MM-DD (returns '' if incomplete)
function parseDateBR(val: string): string {
  const d = val.replace(/\D/g, '');
  if (d.length < 8) return '';
  return `${d.slice(4, 8)}-${d.slice(2, 4)}-${d.slice(0, 2)}`;
}

// YYYY-MM-DD → dd/mm/yyyy
function toBR(iso: string): string {
  if (!iso) return '';
  const [y, m, d] = iso.split('-');
  return `${d}/${m}/${y}`;
}

function buildDatetime(isoDate: string, time: string, endOfDay = false): Date | null {
  if (!isoDate) return null;
  const [y, m, d] = isoDate.split('-').map(Number);
  if (!time) {
    return endOfDay
      ? new Date(y, m - 1, d, 23, 59, 59, 999)
      : new Date(y, m - 1, d, 0, 0, 0, 0);
  }
  const [h, min] = time.split(':').map(Number);
  return new Date(y, m - 1, d, h, min, endOfDay ? 59 : 0);
}

const PRESETS: { key: PresetFilter; label: string }[] = [
  { key: 'active', label: 'Ativos' },
  { key: 'today', label: 'Hoje' },
  { key: 'yesterday', label: 'Ontem' },
  { key: '2days', label: 'Últimos 2 dias' },
  { key: '7days', label: 'Últimos 7 dias' },
  { key: 'custom', label: 'Período' },
];

function getPresetRange(preset: PresetFilter): { from: Date; to: Date } | null {
  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0);
  switch (preset) {
    case 'today': return { from: startOfToday, to: now };
    case 'yesterday': {
      const f = new Date(startOfToday); f.setDate(f.getDate() - 1);
      const t = new Date(f); t.setHours(23, 59, 59, 999);
      return { from: f, to: t };
    }
    case '2days': {
      const f = new Date(startOfToday); f.setDate(f.getDate() - 1);
      return { from: f, to: now };
    }
    case '7days': {
      const f = new Date(startOfToday); f.setDate(f.getDate() - 6);
      return { from: f, to: now };
    }
    default: return null;
  }
}

// ─── DateTimeInput ──────────────────────────────────────────────────────────
interface DateTimeInputProps {
  dateValue: string;       // YYYY-MM-DD
  timeValue: string;       // HH:mm
  onDateChange: (v: string) => void;
  onTimeChange: (v: string) => void;
  lightTheme?: boolean;
}

const DateTimeInput: React.FC<DateTimeInputProps> = ({ dateValue, timeValue, onDateChange, onTimeChange, lightTheme }) => {
  const [typed, setTyped] = useState(toBR(dateValue));

  useEffect(() => { setTyped(toBR(dateValue)); }, [dateValue]);

  const base = cn(
    "text-xs rounded-lg px-2.5 py-1.5 border focus:outline-none focus:ring-1 focus:ring-indigo-500",
    lightTheme ? "bg-white border-gray-300 text-gray-800 placeholder-gray-400" : "bg-gray-800 border-gray-700 text-gray-200 placeholder-gray-500"
  );

  return (
    <div className="flex items-center gap-1">
      <input
        type="text"
        placeholder="dd/mm/aaaa"
        value={typed}
        onChange={e => {
          const formatted = formatDateTyped(e.target.value);
          setTyped(formatted);
          const parsed = parseDateBR(formatted);
          if (parsed) onDateChange(parsed);
          else if (!formatted) onDateChange('');
        }}
        onBlur={() => {
          const parsed = parseDateBR(typed);
          if (parsed) { onDateChange(parsed); setTyped(toBR(parsed)); }
          else if (!typed) onDateChange('');
        }}
        className={cn(base, "w-28")}
      />
      <input
        type="time"
        value={timeValue}
        onChange={e => onTimeChange(e.target.value)}
        className={cn(base, "w-24")}
      />
    </div>
  );
};

// ─── Main component ─────────────────────────────────────────────────────────
const LivroPanel: React.FC<LivroPanelProps> = ({ customerId, onClose, lightTheme, canManage = true }) => {
  const { notes, loading, createNote, refreshNotes } = useCustomerNotes(customerId, {
    noteType: 'livro',
    activeOnly: false,
  });

  // ─── A page as a thread ───────────────────────────────────────────────────
  //
  // The apps have shown this for a while; the web showed content, author and timestamp and
  // stopped. So a page concluded on a phone — with a reason, by a named síndico — read here
  // exactly like one nobody had touched.
  const [filter, setFilter] = useState<LivroFilter>(DEFAULT_LIVRO_FILTER);
  const [openNoteId, setOpenNoteId] = useState<string | null>(null);
  const [entries, setEntries] = useState<LivroNoteEntry[]>([]);
  const [entriesLoading, setEntriesLoading] = useState(false);
  const [draft, setDraft] = useState('');
  const [writing, setWriting] = useState(false);

  const visibleNotes = useMemo(() => matching(notes, filter), [notes, filter]);

  const loadEntries = useCallback(async (noteId: string) => {
    setEntriesLoading(true);
    setEntries([]);
    try {
      const response = await condoApi.getCustomerNoteEntries(customerId, noteId);
      setEntries(orderedEntries(response?.data?.data ?? []));
    } catch {
      // The thread failing to load must not hide the page it belongs to; the entries area says so.
      setEntries([]);
    } finally {
      setEntriesLoading(false);
    }
  }, [customerId]);

  const toggleNote = useCallback((noteId: string) => {
    setDraft('');
    setOpenNoteId(current => {
      if (current === noteId) return null;
      void loadEntries(noteId);
      return noteId;
    });
  }, [loadEntries]);

  /** Comment / conclude / reopen all write the same thing — words — and all re-read afterwards. */
  const writeToPage = useCallback(async (
    note: CustomerNote,
    kind: 'comment' | 'conclude' | 'reopen',
  ) => {
    const text = draft.trim();
    if (!canSubmitEntry(text)) return;
    setWriting(true);
    try {
      if (kind === 'conclude') {
        await condoApi.concludeCustomerNote(customerId, note.id, text);
      } else if (kind === 'reopen') {
        await condoApi.reopenCustomerNote(customerId, note.id, text);
      } else {
        await condoApi.addCustomerNoteEntry(customerId, note.id, text);
      }
      setDraft('');
      toast.success(
        kind === 'conclude' ? 'Ocorrência concluída'
          : kind === 'reopen' ? 'Ocorrência reaberta'
          : 'Comentário adicionado'
      );
      // Both halves: the thread this page shows, and the list behind it — without the second the
      // card keeps the chip it had before.
      await loadEntries(note.id);
      await refreshNotes?.();
    } catch {
      toast.error('Não foi possível salvar');
    } finally {
      setWriting(false);
    }
  }, [customerId, draft, loadEntries, refreshNotes]);

  const [newContent, setNewContent] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSummary, setShowSummary] = useState(false);

  // Photo attachment for the entry being drafted
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [photoUrl, setPhotoUrl] = useState<string | null>(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [showPhotoActionModal, setShowPhotoActionModal] = useState(false);
  const [showPhotoUploadModal, setShowPhotoUploadModal] = useState(false);
  const [showPhotoWebcamModal, setShowPhotoWebcamModal] = useState(false);

  const handlePhotoResult = async (result: PhotoResult) => {
    if (!customerId) return;
    setPhotoPreview(result.base64);
    setUploadingPhoto(true);
    try {
      const file = new File([result.blob], 'livro.jpg', { type: 'image/jpeg' });
      const formData = new FormData();
      formData.append('photo', await normalizePhotoForUpload(file));
      const res = await condoApi.uploadCustomerNotePhoto(customerId, formData);
      setPhotoUrl(res.data.photo_url);
    } catch {
      toast.error('Erro ao enviar foto');
      setPhotoPreview(null);
    } finally {
      setUploadingPhoto(false);
    }
  };

  // Filter state
  const [preset, setPreset] = useState<PresetFilter>('active');
  const [dateFrom, setDateFrom] = useState('');
  const [timeFrom, setTimeFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [timeTo, setTimeTo] = useState('');

  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const modalTextareaRef = useRef<HTMLTextAreaElement>(null);
  // Full-screen composer. The inline box is sized for the two-line entries that make up most of
  // the livro; anything longer than that is written here instead of inside a 3-row window.
  const [showComposer, setShowComposer] = useState(false);

  useEffect(() => { textareaRef.current?.focus(); }, []);

  // Grow the inline box with its content instead of hiding earlier lines behind a scrollbar.
  // useLayoutEffect so the resize lands in the same frame as the keystroke — with useEffect the
  // box visibly lags a character behind while typing fast.
  useLayoutEffect(() => {
    const el = textareaRef.current;
    if (!el || showComposer) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(Math.max(el.scrollHeight, COMPOSER_MIN_PX), COMPOSER_MAX_PX)}px`;
  }, [newContent, showComposer]);

  useEffect(() => {
    if (!showComposer) return;
    const el = modalTextareaRef.current;
    if (!el) return;
    el.focus();
    // Caret to the end, not the start — the modal is usually opened to continue writing.
    el.setSelectionRange(el.value.length, el.value.length);
  }, [showComposer]);

  const handleSubmit = useCallback(async () => {
    const content = newContent.trim();
    if (content.length < LIVRO_MIN_CHARS || content.length > LIVRO_MAX_CHARS || isSubmitting || uploadingPhoto) return;
    setIsSubmitting(true);
    try {
      await createNote(content, photoUrl || undefined);
      setNewContent('');
      setPhotoPreview(null);
      setPhotoUrl(null);
      // Only on success: a failed submit must keep the text on screen, in the composer the
      // operator was writing it in.
      setShowComposer(false);
    }
    catch (err: any) { console.error('Error creating livro entry', err); }
    finally { setIsSubmitting(false); }
  }, [newContent, isSubmitting, uploadingPhoto, createNote, photoUrl]);

  const clearCustom = () => { setDateFrom(''); setTimeFrom(''); setDateTo(''); setTimeTo(''); };

  // Validity is computed from the trimmed text so trailing spaces neither satisfy the minimum
  // nor trip the maximum, while the counter reflects what maxLength is actually limiting.
  const trimmedLength = newContent.trim().length;
  const tooShort = trimmedLength > 0 && trimmedLength < LIVRO_MIN_CHARS;
  const canSubmit = trimmedLength >= LIVRO_MIN_CHARS && !isSubmitting && !uploadingPhoto;

  const counterClass = (light?: boolean) => cn(
    "text-[11px] tabular-nums",
    newContent.length >= LIVRO_MAX_CHARS
      ? "text-amber-500 font-semibold"
      : light ? "text-gray-400" : "text-gray-500"
  );

  const hint = tooShort
    ? `Mínimo de ${LIVRO_MIN_CHARS} caracteres`
    : null;

  const summaryNotes = useMemo(() => {
    let from: Date | null = null;
    let to: Date | null = null;

    if (preset === 'custom') {
      from = buildDatetime(dateFrom, timeFrom, false);
      to = buildDatetime(dateTo, timeTo, true);
    } else if (preset !== 'active') {
      const range = getPresetRange(preset);
      if (range) { from = range.from; to = range.to; }
    }

    return notes
      .filter(n => {
        if (preset === 'active' && !n.is_active) return false;
        const d = new Date(n.created_at);
        if (from && d < from) return false;
        if (to && d > to) return false;
        return true;
      })
      .sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
  }, [notes, preset, dateFrom, timeFrom, dateTo, timeTo]);

  const periodLabel = useMemo(() => {
    if (preset === 'active') return 'Ocorrências Ativas';
    if (preset === 'today') return 'Hoje';
    if (preset === 'yesterday') return 'Ontem';
    if (preset === '2days') return 'Últimos 2 dias';
    if (preset === '7days') return 'Últimos 7 dias';
    const f = [toBR(dateFrom) || 'início', timeFrom].filter(Boolean).join(' ');
    const t = [toBR(dateTo) || 'hoje', timeTo].filter(Boolean).join(' ');
    return `${f} a ${t}`;
  }, [preset, dateFrom, timeFrom, dateTo, timeTo]);

  const summaryText = useMemo(() => {
    const header = `LIVRO DE OCORRÊNCIAS — ${periodLabel}`;
    if (!summaryNotes.length) return `${header}\n${'─'.repeat(40)}\n\nNenhuma ocorrência registrada.`;
    const entries = summaryNotes.map(n => {
      const status = n.is_active ? '' : ' [DESATIVADA]';
      return `[${formatTimestamp(n.created_at)} — ${n.created_by_name}]${status}\n${n.content}`;
    }).join('\n\n');
    return `${header}\n${'─'.repeat(40)}\n\n${entries}`;
  }, [summaryNotes, periodLabel]);

  const handlePrint = () => {
    const win = window.open('', '_blank');
    if (!win) return;
    win.document.write(`<!DOCTYPE html><html><head>
      <meta charset="utf-8"/><title>Livro de Ocorrências</title>
      <style>
        body{font-family:Arial,sans-serif;padding:32px;color:#111;font-size:13px}
        h1{font-size:16px;font-weight:bold;margin-bottom:4px}
        .sub{font-size:11px;color:#555;margin-bottom:24px}
        hr{border:none;border-top:1px solid #ccc;margin:12px 0 20px}
        .entry{margin-bottom:18px;page-break-inside:avoid}
        .meta{font-size:11px;color:#4f46e5;margin-bottom:4px;font-weight:600}
        .content{white-space:pre-wrap;line-height:1.6}
        .inactive{opacity:.5;text-decoration:line-through}
      </style>
    </head><body>
      <h1>Livro de Ocorrências</h1>
      <div class="sub">${periodLabel} · Impresso em ${formatTimestamp(new Date().toISOString())}</div>
      <hr/>
      ${summaryNotes.map(n => `
        <div class="entry">
          <div class="meta">${formatTimestamp(n.created_at)} — ${n.created_by_name}${!n.is_active ? ' [DESATIVADA]' : ''}</div>
          <div class="content${!n.is_active ? ' inactive' : ''}">${n.content.replace(/</g,'&lt;').replace(/>/g,'&gt;')}</div>
        </div>`).join('')}
    </body></html>`);
    win.document.close(); win.focus(); win.print();
  };

  const handleWhatsApp = () =>
    window.open('https://wa.me/?text=' + encodeURIComponent(summaryText), '_blank');

  const chipBase ="px-2.5 py-1 rounded-full text-xs font-medium border transition-colors";
  const chipActive = "bg-indigo-600 text-white border-indigo-600";
  const chipInactive = lightTheme
    ? "bg-white text-gray-600 border-gray-300 hover:border-indigo-400 hover:text-indigo-600"
    : "bg-gray-800 text-gray-400 border-gray-700 hover:border-indigo-500 hover:text-indigo-300";

  return (
    <div className={cn(
      // h-full so the panel fills the standalone /livro page too; inside the Operational View's
      // grid the item already stretches, so it changes nothing there. The col-span classes are
      // inert outside that grid.
      "lg:col-span-3 2xl:col-span-4 h-full flex flex-col min-h-0 overflow-hidden rounded-xl border",
      lightTheme ? "bg-white border-gray-200" : "bg-gray-950 border-gray-800"
    )}>
      {/* Header — same back-arrow convention as every other main-frame page (Ocorrências, Normas, etc.), not a modal-style X */}
      <div className={cn("flex items-center gap-3 px-4 py-3 border-b flex-shrink-0", lightTheme ? "border-gray-200" : "border-gray-800")}>
        {onClose && (
          <button
            onClick={onClose}
            title="Voltar"
            aria-label="Voltar"
            className={cn("p-1.5 rounded-lg transition-colors", lightTheme ? "hover:bg-gray-100 text-gray-600" : "hover:bg-gray-800 text-gray-400")}
          >
            <ArrowLeft size={18} />
          </button>
        )}
        <div className="flex items-center gap-2">
          <h2 className={cn("text-base font-semibold uppercase tracking-wide", lightTheme ? "text-gray-900" : "text-white")}>Livro de Ocorrências</h2>
        </div>
        {/* A livro is a BOOK: its finished pages stay in it. "What is still pending" and "what
            happened in March" are both real questions, so the book is the default and the other
            two readings are one click away. */}
        <div className="flex items-center gap-1 ml-3">
          {LIVRO_FILTERS.map(option => (
            <button
              key={option}
              onClick={() => setFilter(option)}
              className={cn(
                "px-2.5 py-1 rounded-lg text-[11px] font-semibold border transition-colors",
                filter === option
                  ? (lightTheme
                      ? "bg-indigo-600 text-white border-indigo-600"
                      : "bg-indigo-500 text-white border-indigo-500")
                  : (lightTheme
                      ? "bg-white text-gray-600 border-gray-300 hover:bg-gray-100"
                      : "bg-gray-900 text-gray-400 border-gray-700 hover:bg-gray-800")
              )}
            >
              {filterLabel(option)}
            </button>
          ))}
        </div>
        <div className="flex-1" />
        <button
          onClick={() => setShowSummary(true)}
          className={cn(
            "flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors",
            lightTheme
              ? "bg-indigo-50 hover:bg-indigo-100 text-indigo-700 border-indigo-200"
              : "bg-indigo-900/40 hover:bg-indigo-900/70 text-indigo-300 border-indigo-700"
          )}
        >
          <FileText className="h-3.5 w-3.5" /> Resumo
        </button>
      </div>

      {/* Entry form */}
      <div className={cn("px-4 py-3 border-b flex-shrink-0", lightTheme ? "border-gray-200" : "border-gray-800")}>
        <div className="relative">
          <textarea
            ref={textareaRef}
            value={newContent}
            onChange={e => setNewContent(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) { e.preventDefault(); handleSubmit(); } }}
            placeholder="Registrar nova ocorrência... (Ctrl+Enter para enviar)"
            maxLength={LIVRO_MAX_CHARS}
            style={{ minHeight: COMPOSER_MIN_PX, maxHeight: COMPOSER_MAX_PX }}
            className={cn(
              // pr-11 keeps the growing text clear of the expand button pinned top-right.
              "w-full rounded-lg pl-3 pr-11 py-2 text-sm leading-relaxed focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 resize-none border overflow-y-auto custom-scrollbar",
              lightTheme ? "bg-gray-50 border-gray-300 text-gray-900 placeholder-gray-400" : "bg-gray-900 border-gray-700 text-white placeholder-gray-500"
            )}
          />
          <button
            type="button"
            onClick={() => setShowComposer(true)}
            title="Escrever em tela cheia"
            aria-label="Escrever em tela cheia"
            className={cn(
              "absolute top-1.5 right-1.5 p-1.5 rounded-md transition-colors",
              lightTheme
                ? "text-gray-400 hover:text-indigo-600 hover:bg-indigo-50"
                : "text-gray-500 hover:text-indigo-300 hover:bg-indigo-900/30"
            )}
          >
            <Maximize2 className="h-3.5 w-3.5" />
          </button>
        </div>
        <div className="flex items-center justify-between mt-2">
          {photoPreview ? (
            <div className="relative rounded-lg overflow-hidden border border-gray-700/50 w-16 h-16 shrink-0">
              <img src={photoPreview} alt="preview" className="w-full h-full object-cover" />
              {uploadingPhoto && (
                <div className="absolute inset-0 flex items-center justify-center bg-black/50">
                  <Loader2 className="h-4 w-4 animate-spin text-white" />
                </div>
              )}
              <button
                type="button"
                onClick={() => { setPhotoPreview(null); setPhotoUrl(null); }}
                className="absolute top-0.5 right-0.5 p-0.5 bg-black/60 hover:bg-black/80 rounded text-white transition-colors"
              >
                <Trash2 className="h-3 w-3" />
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setShowPhotoActionModal(true)}
              className={cn(
                "flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium border transition-colors",
                lightTheme ? "bg-gray-50 hover:bg-gray-100 text-gray-600 border-gray-300" : "bg-gray-900 hover:bg-gray-800 text-gray-400 border-gray-700"
              )}
            >
              <Camera className="h-3.5 w-3.5" /> Adicionar foto
            </button>
          )}
          <div className="flex items-center gap-3">
            {hint && <span className="text-[11px] text-amber-500">{hint}</span>}
            <span className={counterClass(lightTheme)}>{newContent.length}/{LIVRO_MAX_CHARS}</span>
            <button
              onClick={handleSubmit}
              disabled={!canSubmit}
              className={cn(
                "flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-sm font-semibold transition-colors",
                canSubmit
                  ? "bg-indigo-600 hover:bg-indigo-700 text-white"
                  : lightTheme ? "bg-gray-100 text-gray-400 cursor-not-allowed" : "bg-gray-800 text-gray-500 cursor-not-allowed"
              )}
            >
              <Plus className="h-4 w-4" />
              {isSubmitting ? 'Registrando...' : 'Registrar'}
            </button>
          </div>
        </div>
      </div>

      {/* Notes list */}
      <div className="flex-1 overflow-y-auto custom-scrollbar px-4 py-3 space-y-3">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500" />
          </div>
        ) : visibleNotes.length === 0 ? (
          <div className={cn("flex flex-col items-center justify-center py-12", lightTheme ? "text-gray-400" : "text-gray-500")}>
            <BookOpen className="h-10 w-10 mb-3 opacity-30" />
            <p className="text-sm">
              {notes.length === 0
                ? 'Nenhuma ocorrência registrada.'
                : `Nada em ${filterLabel(filter)}.`}
            </p>
          </div>
        ) : (
          <>
            {visibleNotes.map(note => {
              const isOpen = openNoteId === note.id;
              const hint = writingHint(note, draft);
              return (
              <div key={note.id} className={cn("rounded-lg border p-3", lightTheme ? "border-gray-200 bg-gray-50" : "border-gray-800 bg-gray-900")}>
                {note.photo_url && (
                  <img
                    src={makeAbsolutePhotoUrl(note.photo_url)}
                    alt="foto"
                    className="w-20 h-20 object-cover rounded-lg border border-gray-700/30 mb-2 cursor-pointer"
                    onClick={() => window.open(makeAbsolutePhotoUrl(note.photo_url!), '_blank')}
                  />
                )}
                {/* Whether a page is still open is the first thing anybody reading the book wants,
                    so it is on the card rather than something to be discovered by opening it. */}
                <div className="flex items-start gap-2 mb-1.5">
                  <div className={cn(
                    "px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide flex-shrink-0",
                    note.is_active
                      ? (lightTheme ? "bg-amber-100 text-amber-800" : "bg-amber-900/40 text-amber-300")
                      : (lightTheme ? "bg-emerald-100 text-emerald-800" : "bg-emerald-900/40 text-emerald-300")
                  )}>
                    {statusLabel(note)}
                  </div>
                </div>
                <NoteContent content={note.content} lightTheme={lightTheme} />
                <div className={cn("mt-1.5 text-[11px]", lightTheme ? "text-gray-400" : "text-gray-500")}>
                  {note.created_by_name} · {formatTimestamp(note.created_at)}
                </div>

                {/* The closure IS the answer to "what happened" — it was in the payload all along
                    and simply never rendered here. */}
                {!note.is_active && (note.concluded_reason || note.concluded_by_name) && (
                  <div className={cn(
                    "mt-2 rounded-md border-l-2 pl-2 py-1 text-[11px]",
                    lightTheme ? "border-emerald-400 text-gray-600" : "border-emerald-600 text-gray-300"
                  )}>
                    {note.concluded_reason && <div>{note.concluded_reason}</div>}
                    <div className={cn(lightTheme ? "text-gray-400" : "text-gray-500")}>
                      Concluída por {note.concluded_by_name || '—'}
                      {note.concluded_at ? ` · ${formatTimestamp(note.concluded_at)}` : ''}
                    </div>
                  </div>
                )}

                <button
                  onClick={() => toggleNote(note.id)}
                  aria-label={`${isOpen ? 'Ocultar' : 'Ver'} acompanhamento de ${note.content}`}
                  className={cn(
                    "mt-2 flex items-center gap-1 text-[11px] font-semibold transition-colors",
                    lightTheme ? "text-indigo-700 hover:text-indigo-900" : "text-indigo-300 hover:text-indigo-100"
                  )}
                >
                  {isOpen ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
                  {isOpen ? 'Ocultar acompanhamento' : 'Ver acompanhamento'}
                </button>

                {isOpen && (
                  <div className={cn("mt-2 pt-2 border-t", lightTheme ? "border-gray-200" : "border-gray-800")}>
                    {entriesLoading ? (
                      <div className="flex items-center gap-2 py-2 text-[11px] text-gray-400">
                        <Loader2 size={12} className="animate-spin" /> Carregando acompanhamento…
                      </div>
                    ) : entries.length === 0 ? (
                      <div className={cn("py-1 text-[11px]", lightTheme ? "text-gray-400" : "text-gray-500")}>
                        Nenhum acompanhamento ainda.
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {entries.map(entry => (
                          <div key={entry.id} className="flex gap-2">
                            <span className={cn(
                              "mt-0.5 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wide flex-shrink-0 h-fit",
                              entry.kind === 'concluded'
                                ? (lightTheme ? "bg-emerald-100 text-emerald-700" : "bg-emerald-900/40 text-emerald-300")
                                : entry.kind === 'reopened'
                                  ? (lightTheme ? "bg-amber-100 text-amber-700" : "bg-amber-900/40 text-amber-300")
                                  : (lightTheme ? "bg-gray-200 text-gray-600" : "bg-gray-800 text-gray-400")
                            )}>
                              {kindLabel(entry.kind)}
                            </span>
                            <div className="min-w-0">
                              <div className={cn("text-xs", lightTheme ? "text-gray-800" : "text-gray-200")}>{entry.body}</div>
                              <div className={cn("text-[10px]", lightTheme ? "text-gray-400" : "text-gray-500")}>
                                {entry.author_name || '—'} · {formatTimestamp(entry.created_at)}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* One field, three intents: a comment, a closure and a reopening all ask for
                        the same thing — words. */}
                    <textarea
                      value={draft}
                      onChange={e => setDraft(e.target.value.slice(0, LIVRO_ENTRY_MAX_CHARS))}
                      placeholder="Acrescentar ao acompanhamento"
                      rows={2}
                      disabled={writing}
                      className={cn(
                        "mt-2 w-full rounded-md border px-2 py-1.5 text-xs resize-none",
                        lightTheme
                          ? "bg-white border-gray-300 text-gray-900 placeholder-gray-400"
                          : "bg-gray-950 border-gray-700 text-white placeholder-gray-600"
                      )}
                    />
                    {/* The actions are unavailable until the entry is long enough, so the rule and
                        the count stay on screen — an unavailable control with no stated reason is
                        a dead button, which is how this was reported on mobile. */}
                    <div className="flex items-center justify-between mt-1">
                      <span className={cn("text-[10px]", lightTheme ? "text-gray-500" : "text-gray-500")}>{hint ?? ''}</span>
                      <span className={cn("text-[10px] tabular-nums", lightTheme ? "text-gray-400" : "text-gray-500")}>
                        {entryCounter(draft)}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 mt-1.5">
                      <button
                        onClick={() => writeToPage(note, 'comment')}
                        disabled={writing || !canSubmitEntry(draft)}
                        className={cn(
                          "flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors disabled:opacity-40 disabled:cursor-not-allowed",
                          lightTheme
                            ? "bg-white hover:bg-gray-100 text-gray-700 border-gray-300"
                            : "bg-gray-900 hover:bg-gray-800 text-gray-200 border-gray-700"
                        )}
                      >
                        <MessageSquare size={13} /> Comentar
                      </button>
                      {canCloseLivroPage(note, canManage) && (
                        <button
                          onClick={() => writeToPage(note, 'conclude')}
                          disabled={writing || !canSubmitEntry(draft)}
                          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold bg-emerald-600 hover:bg-emerald-700 text-white transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                          <CheckCircle2 size={13} /> Concluir
                        </button>
                      )}
                      {canReopenLivroPage(note, canManage) && (
                        <button
                          onClick={() => writeToPage(note, 'reopen')}
                          disabled={writing || !canSubmitEntry(draft)}
                          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold bg-amber-600 hover:bg-amber-700 text-white transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                          <RotateCcw size={13} /> Reabrir
                        </button>
                      )}
                      {writing && <Loader2 size={14} className="animate-spin text-gray-400" />}
                    </div>
                  </div>
                )}
              </div>
            );})}
          </>
        )}
      </div>

      {/* Full-screen composer — the room the inline box cannot give without burying the entries */}
      {showComposer && (
        <div
          className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
          onClick={() => setShowComposer(false)}
        >
          <div
            className={cn(
              "w-full max-w-3xl rounded-xl border shadow-2xl flex flex-col max-h-[88vh]",
              lightTheme ? "bg-white border-gray-200" : "bg-gray-900 border-gray-700"
            )}
            onClick={e => e.stopPropagation()}
            onKeyDown={e => {
              if (e.key === 'Escape') { e.stopPropagation(); setShowComposer(false); }
              if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) { e.preventDefault(); handleSubmit(); }
            }}
          >
            <div className={cn("flex items-center justify-between px-4 py-3 border-b flex-shrink-0", lightTheme ? "border-gray-200" : "border-gray-800")}>
              <div className="flex items-center gap-2">
                <BookOpen className={cn("h-4 w-4", lightTheme ? "text-indigo-600" : "text-indigo-400")} />
                <h3 className={cn("text-sm font-semibold", lightTheme ? "text-gray-900" : "text-white")}>Nova ocorrência</h3>
              </div>
              <button
                onClick={() => setShowComposer(false)}
                title="Fechar"
                className={cn("p-1.5 rounded-lg transition-colors", lightTheme ? "text-gray-500 hover:text-gray-900 hover:bg-gray-100" : "text-gray-400 hover:text-white hover:bg-gray-800")}
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="flex-1 min-h-0 p-4">
              <textarea
                ref={modalTextareaRef}
                value={newContent}
                onChange={e => setNewContent(e.target.value)}
                placeholder="Descreva a ocorrência..."
                maxLength={LIVRO_MAX_CHARS}
                className={cn(
                  "w-full h-full min-h-[30vh] rounded-lg px-3 py-2 text-sm leading-relaxed resize-none border overflow-y-auto custom-scrollbar focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500",
                  lightTheme ? "bg-gray-50 border-gray-300 text-gray-900 placeholder-gray-400" : "bg-gray-950 border-gray-700 text-white placeholder-gray-500"
                )}
              />
            </div>

            <div className={cn("flex items-center justify-between gap-3 px-4 py-3 border-t flex-shrink-0", lightTheme ? "border-gray-200" : "border-gray-800")}>
              <div className="flex items-center gap-3 min-w-0">
                {photoPreview ? (
                  <div className="relative rounded-lg overflow-hidden border border-gray-700/50 w-12 h-12 shrink-0">
                    <img src={photoPreview} alt="preview" className="w-full h-full object-cover" />
                    {uploadingPhoto && (
                      <div className="absolute inset-0 flex items-center justify-center bg-black/50">
                        <Loader2 className="h-4 w-4 animate-spin text-white" />
                      </div>
                    )}
                    <button
                      type="button"
                      onClick={() => { setPhotoPreview(null); setPhotoUrl(null); }}
                      className="absolute top-0.5 right-0.5 p-0.5 bg-black/60 hover:bg-black/80 rounded text-white transition-colors"
                    >
                      <Trash2 className="h-3 w-3" />
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => setShowPhotoActionModal(true)}
                    className={cn(
                      "flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium border transition-colors",
                      lightTheme ? "bg-gray-50 hover:bg-gray-100 text-gray-600 border-gray-300" : "bg-gray-900 hover:bg-gray-800 text-gray-400 border-gray-700"
                    )}
                  >
                    <Camera className="h-3.5 w-3.5" /> Adicionar foto
                  </button>
                )}
                <span className={cn("text-[11px] hidden sm:block", lightTheme ? "text-gray-400" : "text-gray-500")}>
                  Ctrl+Enter para registrar
                </span>
              </div>
              <div className="flex items-center gap-3 flex-shrink-0">
                {hint && <span className="text-[11px] text-amber-500">{hint}</span>}
                <span className={counterClass(lightTheme)}>{newContent.length}/{LIVRO_MAX_CHARS}</span>
                <button
                  onClick={handleSubmit}
                  disabled={!canSubmit}
                  className={cn(
                    "flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold transition-colors",
                    canSubmit
                      ? "bg-indigo-600 hover:bg-indigo-700 text-white"
                      : lightTheme ? "bg-gray-100 text-gray-400 cursor-not-allowed" : "bg-gray-800 text-gray-500 cursor-not-allowed"
                  )}
                >
                  <Plus className="h-4 w-4" />
                  {isSubmitting ? 'Registrando...' : 'Registrar'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Summary modal */}
      {showSummary && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setShowSummary(false)}>
          <div
            className={cn("w-full max-w-xl rounded-xl border shadow-2xl flex flex-col max-h-[88vh]", lightTheme ? "bg-white border-gray-200" : "bg-gray-900 border-gray-700")}
            onClick={e => e.stopPropagation()}
          >
            {/* Modal header */}
            <div className={cn("flex items-center justify-between px-4 py-3 border-b flex-shrink-0", lightTheme ? "border-gray-200" : "border-gray-800")}>
              <div className="flex items-center gap-2">
                <FileText className={cn("h-4 w-4", lightTheme ? "text-indigo-600" : "text-indigo-400")} />
                <h3 className={cn("text-sm font-semibold", lightTheme ? "text-gray-900" : "text-white")}>Resumo do Livro</h3>
              </div>
              <div className="flex items-center gap-1.5">
                <button
                  onClick={handlePrint}
                  className={cn("flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-semibold border transition-colors",
                    lightTheme ? "bg-gray-50 hover:bg-gray-100 text-gray-700 border-gray-300" : "bg-gray-800 hover:bg-gray-700 text-gray-300 border-gray-700")}
                >
                  <Printer className="h-3.5 w-3.5" /> Imprimir
                </button>
                <button
                  onClick={handleWhatsApp}
                  className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-semibold border transition-colors bg-green-500 hover:bg-green-600 text-white border-green-500"
                >
                  <Share2 className="h-3.5 w-3.5" /> WhatsApp
                </button>
                <button
                  onClick={() => setShowSummary(false)}
                  className={cn("p-1.5 rounded-lg transition-colors ml-1", lightTheme ? "text-gray-500 hover:text-gray-900 hover:bg-gray-100" : "text-gray-400 hover:text-white hover:bg-gray-800")}
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>

            {/* Preset chips */}
            <div className={cn("px-4 pt-3 pb-2 border-b flex-shrink-0", lightTheme ? "border-gray-200 bg-gray-50" : "border-gray-800 bg-gray-900/50")}>
              <div className="flex flex-wrap gap-1.5 mb-2">
                {PRESETS.map(p => (
                  <button key={p.key} onClick={() => setPreset(p.key)} className={cn(chipBase, preset === p.key ? chipActive : chipInactive)}>
                    {p.key === 'custom' && <Calendar className="inline h-3 w-3 mr-1" />}
                    {p.label}
                  </button>
                ))}
                <span className={cn("ml-auto text-xs self-center", lightTheme ? "text-gray-400" : "text-gray-500")}>
                  {summaryNotes.length} registro{summaryNotes.length !== 1 ? 's' : ''}
                </span>
              </div>

              {/* Custom datetime range — only when Período selected */}
              {preset === 'custom' && (
                <div className={cn("rounded-lg border p-3 mt-1 mb-1", lightTheme ? "bg-white border-gray-200" : "bg-gray-800/60 border-gray-700")}>
                  <div className="flex flex-col gap-2">
                    <div className="flex items-center gap-2">
                      <span className={cn("text-[11px] font-semibold w-6", lightTheme ? "text-gray-500" : "text-gray-400")}>De</span>
                      <DateTimeInput
                        dateValue={dateFrom} timeValue={timeFrom}
                        onDateChange={setDateFrom} onTimeChange={setTimeFrom}
                        lightTheme={lightTheme}
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={cn("text-[11px] font-semibold w-6", lightTheme ? "text-gray-500" : "text-gray-400")}>Até</span>
                      <DateTimeInput
                        dateValue={dateTo} timeValue={timeTo}
                        onDateChange={setDateTo} onTimeChange={setTimeTo}
                        lightTheme={lightTheme}
                      />
                      {(dateFrom || dateTo) && (
                        <button onClick={clearCustom} className={cn("text-xs ml-1", lightTheme ? "text-gray-400 hover:text-gray-700" : "text-gray-500 hover:text-gray-300")}>
                          Limpar
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-4">
              {summaryNotes.length === 0 ? (
                <div className={cn("flex flex-col items-center justify-center py-12", lightTheme ? "text-gray-400" : "text-gray-500")}>
                  <FileText className="h-8 w-8 mb-2 opacity-30" />
                  <p className="text-sm">Nenhuma ocorrência no período.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {summaryNotes.map(note => (
                    <div key={note.id} className={cn("pb-4 border-b last:border-b-0", lightTheme ? "border-gray-100" : "border-gray-800")}>
                      <div className={cn("text-[11px] font-semibold mb-1", lightTheme ? "text-indigo-600" : "text-indigo-400")}>
                        {formatTimestamp(note.created_at)} — {note.created_by_name}
                        {!note.is_active && <span className={cn("ml-2 font-normal", lightTheme ? "text-gray-400" : "text-gray-500")}>[desativada]</span>}
                      </div>
                      <p className={cn("text-sm whitespace-pre-wrap leading-relaxed", !note.is_active && "line-through opacity-50", lightTheme ? "text-gray-700" : "text-gray-200")}>
                        {note.content}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <PhotoActionModal
        open={showPhotoActionModal}
        onClose={() => setShowPhotoActionModal(false)}
        onUpload={() => { setShowPhotoActionModal(false); setShowPhotoUploadModal(true); }}
        onWebcam={() => { setShowPhotoActionModal(false); setShowPhotoWebcamModal(true); }}
        onIPCamera={() => {}}
        title="Foto da Ocorrência"
        darkMode={!lightTheme}
        hideIPCamera
      />
      <PhotoUploadModal
        open={showPhotoUploadModal}
        onClose={() => setShowPhotoUploadModal(false)}
        title="Foto da Ocorrência"
        cropShape="rect"
        aspectRatio={4 / 3}
        darkMode={!lightTheme}
        disableFaceDetection
        onConfirm={async (result: PhotoResult) => { setShowPhotoUploadModal(false); await handlePhotoResult(result); }}
      />
      <PhotoWebcamModal
        open={showPhotoWebcamModal}
        onClose={() => setShowPhotoWebcamModal(false)}
        title="Foto da Ocorrência"
        cropShape="rect"
        aspectRatio={4 / 3}
        darkMode={!lightTheme}
        disableFaceDetection
        onConfirm={async (result: PhotoResult) => { setShowPhotoWebcamModal(false); await handlePhotoResult(result); }}
      />
    </div>
  );
};

// ─── NoteContent ─────────────────────────────────────────────────────────────
/**
 * A livro entry can be a sentence or several paragraphs, and both have to live in the same list.
 *
 * Collapsed, it is capped and scrolls in place so one long entry cannot push every other entry
 * off the screen. "Ver mais" then drops the cap entirely and lets the outer list scroll — reading
 * a long entry through a 112px window was the complaint.
 *
 * The toggle only appears when the text genuinely overflows; measuring (rather than guessing from
 * character count) is what keeps a two-line entry from growing a pointless "Ver mais".
 */
const NoteContent: React.FC<{
  content: string;
  lightTheme?: boolean;
  muted?: boolean;
  strikethrough?: boolean;
}> = ({ content, lightTheme, muted, strikethrough }) => {
  const ref = useRef<HTMLDivElement>(null);
  const [overflows, setOverflows] = useState(false);
  const [expanded, setExpanded] = useState(false);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    // Only meaningful while collapsed — expanded, scrollHeight === clientHeight by definition.
    if (!expanded) setOverflows(el.scrollHeight > el.clientHeight + 1);
  }, [content, expanded]);

  return (
    <div>
      <div
        ref={ref}
        className={cn(
          expanded ? "max-h-none" : "max-h-40 overflow-y-auto custom-scrollbar"
        )}
      >
        <p className={cn(
          "text-sm leading-relaxed whitespace-pre-wrap break-words",
          strikethrough && "line-through",
          muted
            ? (lightTheme ? "text-gray-500" : "text-gray-400")
            : (lightTheme ? "text-gray-900" : "text-white")
        )}>
          {content}
        </p>
      </div>
      {(overflows || expanded) && (
        <button
          type="button"
          onClick={() => setExpanded(v => !v)}
          className={cn(
            "mt-1 text-[11px] font-semibold transition-colors",
            lightTheme ? "text-indigo-600 hover:text-indigo-800" : "text-indigo-400 hover:text-indigo-300"
          )}
        >
          {expanded ? 'Ver menos' : 'Ver mais'}
        </button>
      )}
    </div>
  );
};

export default LivroPanel;
